import React, { useEffect, useState, useMemo } from 'react';
import { Users, UserCheck, DollarSign, Activity, LucideIcon, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Card } from '../ui/card';
import { usersApi, DashboardStats } from '../../api/users';

interface KPICardsProps {
  isDark: boolean;
}

interface KPICardData {
  id: number;
  title: string;
  value: number;
  icon: LucideIcon;
  iconBg: string;
  iconBgDark: string;
  iconColor: string;
  isCurrency?: boolean;
}

export default function KPICards({ isDark }: KPICardsProps) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await usersApi.getDashboardStats();
        setStats(data);
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Не удалось загрузить статистику');
        console.error('Error fetching dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatValue = (value: number, isCurrency = false): string => {
    if (isCurrency) {
      return new Intl.NumberFormat('en-US', {
        style: 'decimal',
        currency: 'UZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }
    return new Intl.NumberFormat('en-US').format(value);
  };

  const kpiData: KPICardData[] = useMemo(() => {
    if (!stats) return [];

    return [
      {
        id: 1,
        title: 'Всего пользователей',
        value: stats.totalUsers,
        icon: Users,
        iconBg: '#FBF3D9',
        iconBgDark: '#3B2F0F',
        iconColor: '#9C7E1B',
      },
      {
        id: 2,
        title: 'С подпиской',
        value: stats.subscribedUsers,
        icon: UserCheck,
        iconBg: '#EAF5EE',
        iconBgDark: '#0F2D1B',
        iconColor: '#1F6F4A',
      },
      {
        id: 3,
        title: 'Выручка',
        value: stats.totalRevenue,
        icon: DollarSign,
        iconBg: '#FFFBEB',
        iconBgDark: '#3D2F0A',
        iconColor: '#B45309',
        isCurrency: true,
      },
      {
        id: 4,
        title: 'Активные сессии',
        value: stats.activeUsersByPeriod[0]?.current || 0,
        icon: Activity,
        iconBg: '#EEF2FF',
        iconBgDark: '#1E1B4B',
        iconColor: '#4F46E5',
      },
    ];
  }, [stats]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Card
            key={i}
            className={cn(
              'p-5 rounded-2xl border-0',
              isDark ? 'bg-[#1A1A22]' : 'bg-white'
            )}
          >
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <Card
          className={cn(
            'p-5 rounded-2xl border-0 col-span-full',
            isDark ? 'bg-[#1A1A22]' : 'bg-white'
          )}
        >
          <p className={cn(
            'text-sm',
            isDark ? 'text-red-400' : 'text-red-600'
          )}>
            Ошибка: {error}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
      {kpiData.map((kpi) => {
        const Icon = kpi.icon;

        return (
          <Card
            key={kpi.id}
            className={cn(
              'p-5 rounded-2xl border-0 transition-all duration-300 hover:-translate-y-0.5',
              isDark
                ? 'bg-[#1A1A22] shadow-md shadow-black/30 hover:shadow-lg hover:shadow-black/40'
                : 'bg-white shadow-md shadow-black/[0.04] hover:shadow-lg hover:shadow-black/[0.07]'
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p
                  className={cn(
                    'text-xs font-semibold uppercase tracking-wider',
                    isDark ? 'text-slate-400' : 'text-slate-500'
                  )}
                >
                  {kpi.title}
                </p>
                <p
                  className={cn(
                    'text-2xl lg:text-3xl font-bold mt-2 tracking-tight',
                    isDark ? 'text-white' : 'text-[#111111]'
                  )}
                >
                  {formatValue(kpi.value, kpi.isCurrency)}
                </p>
              </div>
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: isDark ? kpi.iconBgDark : kpi.iconBg }}
              >
                <Icon className="w-5 h-5" style={{ color: kpi.iconColor }} />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
